import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'
import { Check, Loader, X } from 'lucide-react'
import { apiPost } from '../../lib/auth'
import { Button } from '../../components/ui/Button'
import type { ServiceKind } from './ServiceConnectionCard'

export interface ResourcePickerModalProps {
  clientId: string | number
  service: ServiceKind
  googleAccountId: number
  grant?: string
  onDone: () => void
  onCancel: () => void
}

interface ResourceOption {
  key: string
  label: string
  sub?: string
  config: Record<string, unknown>
}

interface Ga4Account {
  accountId: string
  displayName: string
}

interface ServiceAdapter {
  listUrl: (clientId: string | number, googleAccountId: number, accountId?: string) => string
  parseResource: (item: any) => ResourceOption
}

function ga4AccountsUrl(clientId: string | number, googleAccountId: number) {
  return `/api/data-sources/ga4/accounts?clientId=${clientId}&googleAccountId=${googleAccountId}`
}

function appendGrant(url: string, grant?: string) {
  return grant ? `${url}&grant=${grant}` : url
}

interface ListResult {
  data: any[]
  needsAuth: boolean
}

/** Fetch commun pour les endpoints de listing : distingue explicitement l'échec HTTP
 * (res.ok=false → lève une erreur avec le message backend) du besoin de reconnexion
 * (needsAuth=true) et de la liste vraiment vide. Ne masque plus jamais une erreur ou
 * un needsAuth en liste vide silencieuse. */
async function fetchResourceList(url: string): Promise<ListResult> {
  const res = await fetch(url, { credentials: 'include' })
  let json: any = {}
  try {
    json = await res.json()
  } catch {
    json = {}
  }
  if (!res.ok) {
    throw new Error(json?.error || `Erreur ${res.status}`)
  }
  return { data: (json?.data ?? []) as any[], needsAuth: json?.needsAuth === true }
}

/** Humanise une siteUrl GSC pour l'affichage : retire le préfixe sc-domain:, le protocole
 * http(s):// et le slash final. La valeur brute reste utilisée en config (siteUrl). */
export function humanizeSiteUrl(siteUrl: string): string {
  let value = siteUrl.replace(/^sc-domain:/, '')
  value = value.replace(/^https?:\/\//, '')
  value = value.replace(/\/$/, '')
  return value
}

/** Formate un customerId Google Ads (10 chiffres) en groupes 123-456-7890 pour l'affichage.
 * La valeur brute reste utilisée en config (customerId/loginCustomerId). */
export function formatCustomerId(customerId: string): string {
  const digits = customerId.replace(/-/g, '')
  if (!/^\d{10}$/.test(digits)) return customerId
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

const SERVICE_ADAPTERS: Record<ServiceKind, ServiceAdapter> = {
  ga4: {
    // GA4 exige un accountId (compte GA4) en plus du googleAccountId pour lister les properties.
    // La sélection du compte GA4 se fait en amont (voir étape "ga4-accounts" dans le composant),
    // le résultat est passé ici en troisième argument.
    listUrl: (clientId, googleAccountId, accountId) =>
      `/api/data-sources/ga4/properties?clientId=${clientId}&googleAccountId=${googleAccountId}&accountId=${accountId}`,
    parseResource: (item) => ({
      key: item.propertyId,
      label: item.displayName,
      sub: `(${item.propertyId})`,
      config: { propertyId: item.propertyId },
    }),
  },
  gsc: {
    listUrl: (clientId, googleAccountId) =>
      `/api/data-sources/gsc/sites?clientId=${clientId}&googleAccountId=${googleAccountId}`,
    parseResource: (item) => ({
      key: item.siteUrl,
      label: humanizeSiteUrl(item.siteUrl),
      sub: `(${item.siteUrl})`,
      config: { siteUrl: item.siteUrl },
    }),
  },
  ads: {
    listUrl: (clientId, googleAccountId) =>
      `/api/data-sources/ads/accessible-customers?clientId=${clientId}&googleAccountId=${googleAccountId}`,
    parseResource: (item) => ({
      key: item.customerId,
      label: item.name || formatCustomerId(item.customerId),
      sub: `(${formatCustomerId(item.customerId)})`,
      config: { customerId: item.customerId, loginCustomerId: item.loginCustomerId ?? item.customerId },
    }),
  },
  gbp: {
    listUrl: (clientId, googleAccountId) =>
      `/api/data-sources/gbp/locations?clientId=${clientId}&googleAccountId=${googleAccountId}`,
    parseResource: (item) => ({
      key: item.googleId,
      label: item.name,
      sub: item.address || undefined,
      config: { locationId: item.googleId },
    }),
  },
}

const SERVICE_LABELS: Record<ServiceKind, string> = {
  ga4: 'Google Analytics 4',
  gsc: 'Search Console',
  ads: 'Google Ads',
  gbp: "Fiche d'établissement (GBP)",
}

export default function ResourcePickerModal({
  clientId,
  service,
  googleAccountId,
  grant,
  onDone,
  onCancel,
}: ResourcePickerModalProps) {
  const adapter = SERVICE_ADAPTERS[service]
  const isGa4 = service === 'ga4'

  const [resources, setResources] = useState<ResourceOption[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [needsAuth, setNeedsAuth] = useState(false)
  const [search, setSearch] = useState('')

  const [ga4Accounts, setGa4Accounts] = useState<Ga4Account[]>([])
  const [selectedGa4AccountId, setSelectedGa4AccountId] = useState<string | null>(null)

  // Étape 1 (GA4 uniquement) : lister les comptes GA4. Pour les autres services,
  // lister directement les ressources.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setNeedsAuth(false)
    setSearch('')
    setSelectedGa4AccountId(null)
    setSelectedKey(null)

    if (isGa4) {
      fetchResourceList(appendGrant(ga4AccountsUrl(clientId, googleAccountId), grant))
        .then((result) => {
          if (cancelled) return
          setGa4Accounts(result.data as Ga4Account[])
          setNeedsAuth(result.needsAuth)
        })
        .catch((err) => {
          if (!cancelled) {
            setGa4Accounts([])
            setError(err?.message || 'Erreur lors du chargement des comptes GA4.')
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    } else {
      fetchResourceList(appendGrant(adapter.listUrl(clientId, googleAccountId), grant))
        .then((result) => {
          if (cancelled) return
          setResources(result.data.map(adapter.parseResource))
          setNeedsAuth(result.needsAuth)
        })
        .catch((err) => {
          if (!cancelled) {
            setResources([])
            setError(err?.message || 'Erreur lors du chargement des ressources.')
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, clientId, googleAccountId, grant])

  // Étape 2 (GA4 uniquement) : une fois le compte GA4 choisi, lister ses properties.
  useEffect(() => {
    if (!isGa4 || !selectedGa4AccountId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setNeedsAuth(false)
    setSearch('')
    setResources([])
    fetchResourceList(appendGrant(adapter.listUrl(clientId, googleAccountId, selectedGa4AccountId), grant))
      .then((result) => {
        if (cancelled) return
        setResources(result.data.map(adapter.parseResource))
        setNeedsAuth(result.needsAuth)
      })
      .catch((err) => {
        if (!cancelled) {
          setResources([])
          setError(err?.message || 'Erreur lors du chargement des properties GA4.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGa4, selectedGa4AccountId, clientId, googleAccountId, grant])

  function handleSelectGa4Account(accountId: string) {
    setSelectedKey(null)
    setSelectedGa4AccountId(accountId)
  }

  const showingGa4AccountStep = isGa4 && !selectedGa4AccountId

  const searchQuery = search.trim().toLowerCase()
  const filteredGa4Accounts = searchQuery
    ? ga4Accounts.filter((a) => a.displayName.toLowerCase().includes(searchQuery))
    : ga4Accounts
  const filteredResources = searchQuery
    ? resources.filter(
        (r) => r.label.toLowerCase().includes(searchQuery) || (r.sub ?? '').toLowerCase().includes(searchQuery),
      )
    : resources

  async function handleValidate() {
    const selected = resources.find((r) => r.key === selectedKey)
    if (!selected) return
    setSaving(true)
    setError(null)
    const { error: postError } = await apiPost(`/api/data-sources/clients/${clientId}/connections`, {
      service,
      googleAccountId,
      config: selected.config,
      ...(grant ? { grant } : {}),
    })
    setSaving(false)
    if (postError) {
      setError(postError)
      return
    }
    onDone()
  }

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="relative w-full max-w-md max-h-[80vh] overflow-y-auto rounded-2xl border border-[#E2E4ED] bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#E2E4ED] px-5 py-4">
          <h2 className="text-sm font-semibold text-[#1B2559]">
            Sélectionner une ressource — {SERVICE_LABELS[service]}
          </h2>
          <button onClick={onCancel} className="rounded-lg p-1.5 text-[#8B8FA7] hover:bg-[#F4F5FA] hover:text-[#1B2559]">
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-5 py-4">
          {showingGa4AccountStep ? (
            loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-5 h-5 animate-spin text-[#8B8FA7]" strokeWidth={1.5} />
              </div>
            ) : error ? null : needsAuth ? (
              <p className="text-sm text-[#8B8FA7] py-4 text-center">
                Ce compte doit être reconnecté (aucun accès actif).
              </p>
            ) : ga4Accounts.length === 0 ? (
              <p className="text-sm text-[#8B8FA7] py-4 text-center">
                Aucun compte GA4 accessible sur ce compte.
              </p>
            ) : (
              <>
                {ga4Accounts.length > 5 && (
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher..."
                    className="mb-2 w-full rounded-lg border border-[#E2E4ED] px-3 py-2 text-sm text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:border-[#6366F1]/40"
                  />
                )}
                {filteredGa4Accounts.length === 0 ? (
                  <p className="text-sm text-[#8B8FA7] py-4 text-center">
                    Aucun résultat pour cette recherche.
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {filteredGa4Accounts.map((a) => (
                      <button
                        key={a.accountId}
                        type="button"
                        onClick={() => handleSelectGa4Account(a.accountId)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-left border transition-colors border-[#E2E4ED] hover:border-[#6366F1]/40"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#1B2559] truncate">{a.displayName}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )
          ) : (
            <>
              {isGa4 && (
                <button
                  type="button"
                  onClick={() => setSelectedGa4AccountId(null)}
                  className="mb-3 text-xs text-[#6366F1] hover:underline"
                >
                  ← Changer de compte GA4
                </button>
              )}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-5 h-5 animate-spin text-[#8B8FA7]" strokeWidth={1.5} />
                </div>
              ) : error ? null : needsAuth ? (
                <p className="text-sm text-[#8B8FA7] py-4 text-center">
                  Ce compte doit être reconnecté (aucun accès actif).
                </p>
              ) : resources.length === 0 ? (
                <p className="text-sm text-[#8B8FA7] py-4 text-center">
                  Aucune ressource accessible sur ce compte.
                </p>
              ) : (
                <>
                  {resources.length > 5 && (
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Rechercher..."
                      className="mb-2 w-full rounded-lg border border-[#E2E4ED] px-3 py-2 text-sm text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:border-[#6366F1]/40"
                    />
                  )}
                  {filteredResources.length === 0 ? (
                    <p className="text-sm text-[#8B8FA7] py-4 text-center">
                      Aucun résultat pour cette recherche.
                    </p>
                  ) : (
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                      {filteredResources.map((r) => (
                        <button
                          key={r.key}
                          type="button"
                          onClick={() => setSelectedKey(r.key)}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-left border transition-colors ${
                            selectedKey === r.key
                              ? 'border-[#6366F1] bg-[#6366F1]/5'
                              : 'border-[#E2E4ED] hover:border-[#6366F1]/40'
                          }`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1B2559] truncate">{r.label}</p>
                            {r.sub && <p className="text-xs text-[#8B8FA7] truncate mt-0.5">{r.sub}</p>}
                          </div>
                          {selectedKey === r.key && <Check className="w-4 h-4 text-[#6366F1] shrink-0" strokeWidth={2} />}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {error && (
            <p role="alert" className="mt-3 text-xs text-red-500">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[#E2E4ED] px-5 py-4">
          <Button variant="secondary" onClick={onCancel}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleValidate}
            disabled={!selectedKey || saving || resources.length === 0}
            isLoading={saving}
          >
            Valider
          </Button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
